import React from 'react';
import { Button } from './button';

interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({ open, onOpenChange, children }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 dark:bg-black/70" 
        onClick={() => onOpenChange?.(false)}
      />
      {/* Dialog content will be rendered here */}
      {children}
    </div>
  );
};

export const AlertDialogTrigger: React.FC<{ asChild?: boolean; children: React.ReactNode }> = ({ children }) => {
  // In a real implementation, this would handle the trigger logic
  return <>{children}</>;
};

export const AlertDialogContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="relative z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 w-full max-w-md mx-4">
      {children}
    </div>
  );
};

export const AlertDialogHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="mb-4">{children}</div>;
};

export const AlertDialogTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{children}</h2>;
};

export const AlertDialogDescription: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <p className="text-sm text-gray-600 dark:text-gray-400">{children}</p>;
};

export const AlertDialogFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex justify-end gap-2 mt-6">{children}</div>;
};

export const AlertDialogCancel: React.FC<{ 
  children: React.ReactNode; 
  onClick?: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => {
  return (
    <Button variant="outline" onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
};

export const AlertDialogAction: React.FC<{ 
  children: React.ReactNode; 
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}> = ({ children, onClick, className, disabled }) => {
  return (
    <Button onClick={onClick} className={className} disabled={disabled}>
      {children}
    </Button>
  );
};